/**
 * Demo Studio helpers — shared by the Demo Studio list and editor pages.
 */

/**
 * Resolve the public website base that renders demo landing pages, following
 * the environment the portal is currently served from.
 * - Explicit VITE_WEBSITE_BASE_URL override wins (trailing slash stripped).
 * - Else derived from the hostname: localhost → dev website, portal-develop → develop, otherwise production.
 */
export const resolveWebsiteBase = () => {
  const override = import.meta.env.VITE_WEBSITE_BASE_URL;
  if (override) return override.replace(/\/+$/, '');

  if (typeof window === 'undefined') return 'https://aqrate.ca';

  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    return import.meta.env.VITE_WEBSITE_DEV_URL || 'http://localhost:4321';
  }
  if (host.startsWith('portal-develop')) return 'https://develop.aqrate.ca';
  return 'https://aqrate.ca';
};

// Computed once for backward-compat named imports. Falls back to production.
export const WEBSITE_BASE_URL = resolveWebsiteBase();

/**
 * API base the portal talks to — and the origin that now serves public demo pages
 * (the website /demos landing was removed; the API serves {apiBase}/demos/{slug}).
 * Single source of truth: the same env var the axios client uses.
 */
export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000').replace(/\/+$/, '');

/**
 * Parse an organization's demoLandingJson blob into an object, tolerating
 * null / malformed JSON.
 * @param {string|null} json
 * @returns {{headline?: string, subheadline?: string, featuredShareTokens?: string[], demoLoginEmail?: string, demoLoginPassword?: string, published?: boolean}}
 */
export const parseDemoLanding = (json) => {
  try {
    return json ? JSON.parse(json) : {};
  } catch {
    return {};
  }
};

/**
 * Standard viewer password shared by every demo (TASK-0264). Must match the API constant
 * DefaultCredentials.DemoViewerPassword — the server also falls back to it when the create
 * payload leaves the viewer password blank.
 */
export const DEMO_VIEWER_DEFAULT_PASSWORD = 'Demo@View123!';

/**
 * Builder (internal, read-write) login email — derived from the slug.
 * The team logs in with this + the shared demo admin password (server config).
 */
export const demoBuilderEmail = (slug) => `${(slug || '').trim()}-admin@aqrate.ca`;

/** Default viewer (public, read-only) login email — derived from the slug, editable. */
export const demoViewerEmailDefault = (slug) => `${(slug || '').trim()}-demo@aqrate.ca`;

/** Public demo page URL — now served by the API at {apiBase}/demos/{slug} (later demos.aqrate.ca). */
export const demoPublicUrl = (slug) => `${API_BASE_URL}/demos/${slug || ''}`;

/** Pretty subdomain URL ({slug}.aqrate.ca) — live only once the Cloudflare DNS record is provisioned. */
export const demoSubdomainUrl = (slug) => `https://${(slug || '').trim()}.aqrate.ca`;

/** Portal launch link that drops a visitor into the demo login pre-filled. */
export const demoLaunchUrl = (slug) => `${window.location.origin}/login?demo=${slug || ''}`;
